<script>
  import Dialog from '#lib/Dialog.svelte';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import Text from '#lib/Text.svelte';
  import Container from '#lib/Container.svelte';
  import { fetch_entries, fetch_json } from '#lib/util.js';
  import t from '#lib/i18n.svelte.js';
  import { tick, onMount } from 'svelte';
  import { page } from '$app/state';
  import { afterNavigate, goto, invalidate, refreshAll } from '$app/navigation';
  import ImageEntryModel from '#lib/ImageEntryModel.svelte.js';
  import BlockGrid from '#lib/BlockGrid.svelte';
  import SEO from '#lib/SEO.svelte';
  import { getContext } from 'svelte';
  import Alert from '#lib/Alert.svelte';
  import { toast } from '#lib/toast.svelte.js';
  import Toaster from '#lib/Toaster.svelte';
  import PanoramaGridCollage from '#lib/PanoramaGridCollage.svelte';
  import EventPoster from '#lib/EventPoster.svelte';
  import Tag from '#lib/Tag.svelte';
  import CategoryLabel from '#lib/CategoryLabel.svelte';
  import EventTicket from '#lib/EventTicket.svelte';
  import EventRegistration from '#lib/EventRegistration.svelte';
  import EventGuestList from '#lib/EventGuestList.svelte';
  import { lazy } from '#lib/lazy.svelte.js';
  const email_dialog_component = lazy(() => import('#lib/EventRegistrationEmailDialog.svelte'));
  import { get_event_capacity, has_event_concluded, is_event_registration_restricted } from '#lib/event_capacity.js';
  import { get_registration_toast_key } from '#lib/event_registration_client.js';
  import '#lib/system-styles/event-ui.css';

  let site_settings = getContext('site_settings');

  let {
    entry,
    additional_entry_data, // server truth for registrations and seat counts
    is_editing
  } = $props();

  let is_admin = $derived(page.data.current_user?.role === 'admin');
  let is_restricted = $derived(is_event_registration_restricted(entry));
  let has_concluded = $derived(has_event_concluded(entry));

  // The registrations and seat counts every surface below works from. After a
  // change they are refetched on their own (one small query) instead of
  // re-running the whole page load — entry, collections, analytics, and a page
  // view counted for a click. Page data still wins whenever it changes.
  // svelte-ignore state_referenced_locally
  let live_data = $state(additional_entry_data);
  $effect(() => { live_data = additional_entry_data; });
  async function refresh_registrations() {
    try {
      const result = await fetch_json('POST', '/api/event-registration-state', { event_entry_id: entry.entry_id });
      if (result?.success && result.data) {
        live_data = { ...live_data, ...result.data };
        return;
      }
    } catch (err) {
      console.error('Failed to refresh registrations:', err);
    }
    await invalidate('entry:registrations');
  }
  // A page restored from the bfcache (Back from Stripe Checkout) still shows
  // the data from before the member left: the form instead of their booking.
  onMount(() => {
    const on_pageshow = (event) => { if (event.persisted && page.data.current_user) refresh_registrations(); };
    window.addEventListener('pageshow', on_pageshow);
    return () => window.removeEventListener('pageshow', on_pageshow);
  });

  let capacity = $derived(get_event_capacity(entry, live_data));

  // The header follows the first image: one portrait image is the poster
  // beside the title; a landscape image or several are the panorama above
  // it; while editing the empty panorama offers the first image
  let cover = $derived(entry.images?.[0]);
  let image_count = $derived(is_editing ? entry.images_multiref.length : (entry.images?.length ?? 0));
  let layout = $derived(
    image_count === 1 && cover && cover.height >= cover.width ? 'poster'
    : image_count > 0 || is_editing ? 'panorama'
    : 'plain'
  );

  function remove_image(index) {
    // Remove from both arrays at the same index
    entry.images_multiref = entry.images_multiref.filter((_, i) => i !== index);
    entry.images = entry.images.filter((_, i) => i !== index);
  }

  function add_images(entry_ids) {
    for (const entry_id of entry_ids) {
      if (entry.images_multiref.length < 6) {
        entry.images_multiref.push(entry_id);
      }
    }
  }

  // E-mail links and the login return land here with ?show_registration_form=true:
  // open the form right away
  const open_registration_form = Boolean(page.url.searchParams.get('show_registration_form') && page.data.current_user);
  let ticket_element = $state();
  // Run-once guard, only touched inside the afterNavigate callback
  let arrived = false;

  // Such an arrival lands on the hero with the ticket below the fold, whether
  // it shows the open form or the member's registration. Bring the ticket up,
  // once. afterNavigate runs after SvelteKit's own scroll handling (top reset
  // on a client-side navigation, restoration on a load), so it wins; instant,
  // because a page arriving should stand on the ticket, not glide to it.
  afterNavigate(() => {
    if (!open_registration_form || arrived) return;
    arrived = true;
    ticket_element?.scrollIntoView({ block: 'start', behavior: 'instant' });
  });

  let show_detach_dialog = $state(false);
  let email_dialog = $state();

  // Back from Stripe Checkout after paying, the page still carries the seats
  // as a hold whenever the webhook has not landed yet. Until the sync below
  // has confirmed them, the member's card says the payment is being
  // confirmed — not that it is open, with a button to pay once more. Known
  // from the URL, so the server already renders it that way: no flash.
  let confirming_payment = $state(
    page.url.searchParams.get('payment') === 'success' && Boolean(page.data.current_user)
  );

  // Returning from Stripe Checkout (?payment=success|cancelled). Confirm the
  // seats right away from the session instead of hoping the webhook already
  // landed, then strip the params so a reload or Back does not re-toast.
  afterNavigate(async () => {
    const params = page.url.searchParams;
    const payment_return_state = params.get('payment');
    if (!payment_return_state) return;
    const session_id = params.get('session_id');
    confirming_payment = payment_return_state === 'success' && Boolean(page.data.current_user);

    // On the initial page load this callback runs a moment before the router
    // marks itself initialized; give it that moment. Then a navigation to the
    // clean URL, not a shallow replace: that keeps the URL it was called on — the
    // one with the params — as the page URL of the history entry, and every
    // Back to the entry would confirm and toast once more. Nothing loads from
    // the URL, so the navigation costs no request. Should it fail, the
    // confirmation below matters more than a clean URL.
    await tick();
    try {
      const clean = new URL(page.url);
      clean.searchParams.delete('payment');
      clean.searchParams.delete('session_id');
      await goto(clean.pathname + clean.search, { replace: true, reset: false });
    } catch (err) {
      console.error('Could not strip the payment params from the URL:', err);
    }

    if (payment_return_state === 'cancelled') {
      toast.error(t('event_payment_cancelled_notice'), { auto_dismiss: false });
      return;
    }
    if (payment_return_state !== 'success') return;

    let state = 'pending';
    if (session_id) {
      try {
        const result = await fetch_json('POST', '/api/sync-event-payment', { session_id });
        if (result.success && result.state) state = result.state;
      } catch (err) {
        console.error('Failed to sync payment on return from Checkout:', err);
      }
    }
    try {
      await refresh_registrations();
    } finally {
      // Whatever came back is on the card now, so it has the last word
      confirming_payment = false;
    }
    if (state === 'refunded') {
      toast.error(t('event_payment_refunded_notice'), { auto_dismiss: false });
      return;
    }
    const notice = state === 'paid' ? 'event_payment_success_notice'
      : state === 'processing' ? 'event_payment_processing_notice'
      : 'event_payment_pending_notice';
    toast.success(t(notice), { duration: 12000, show_close_icon: true });
  });

  /** After a member changed their registration: refresh and confirm */
  async function handle_registration_changed(counts) {
    await refresh_registrations();
    const notice = page.data.current_user?.email
      ? t('registration_email_notice', { email: page.data.current_user.email })
      : '';
    toast.success([t(get_registration_toast_key(counts)), notice].filter(Boolean).join(' '), {
      duration: 10000,
      show_close_icon: true
    });
  }

  async function detach_from_series() {
    try {
      await fetch_json('POST', '/api/detach-from-series', {
        entry_id: entry.entry_id,
      });
      await refreshAll();
      entry.series_id = undefined;
      entry.series_pattern = undefined;
      entry.series_index = undefined;
      show_detach_dialog = false;
      toast.success(t('detach_from_series_success'));
    } catch (err) {
      console.error('Failed to detach from series:', err);
      toast.error(err.message, { auto_dismiss: false });
    }
  }

  $effect.pre(() => {
    if(entry.images_multiref.filter(Boolean).length > 0) {
      fetch_entries(entry.images_multiref.filter(Boolean)).then(images => {
        entry.images = images.map(image => new ImageEntryModel(image));
      });
    } else {
      entry.images = [];
    }
  });
</script>

<SEO
  title={entry.title}
  description={entry.preview.description || entry.description || site_settings.description}
  image={entry.images?.[0]?.src}
  type='event'
  author={site_settings.name}
/>

<Toaster />

<BlockGrid>


  <!---------------------------------------------------------------------------------->
  <!-------------------------- Event Images ------------------------------------------>
  <!---------------------------------------------------------------------------------->

  {#if layout === 'panorama'}
    <div class='cover-container'>
      <PanoramaGridCollage
        images={entry.images}
        images_multiref={entry.images_multiref}
        {is_editing}
        {site_settings}
        onremove={remove_image}
        onadd={add_images}
      />
    </div>
  {/if}


  <header class='event-header {layout === 'poster' ? '-poster' : '-halves'} my m-block-s'>
    {#if layout === 'poster'}
      <EventPoster
        image={cover}
        bind:images_multiref={entry.images_multiref}
        {is_editing}
        onremove={remove_image}
        onadd={add_images}
      />
    {/if}
    <div class='event-text-column'>

      <!---------------------------------------------------------------------------------->
      <!-------------------------- Event Title ------------------------------------------->
      <!---------------------------------------------------------------------------------->
      <div class='event-title-block flex-column gap-y g-6'>
        {#if is_editing || entry.subtitle || entry.tag_keys.length > 0 || entry.series_id}
          <div class='flex-column items-start gap-y g-6'>
            {#if entry.series_id}
              <div class='flex-wrap gap-x gap-y g-4'>
                <Tag>{t('series_event')}</Tag>
                {#if is_admin}
                  {#if entry.series_index === 0}
                    <Tag class='series-base-tag'>{t('series_base_event')}</Tag>
                  {:else}
                    {#if additional_entry_data?.base_event_url && !is_editing}
                      <a class='event-link text-control text-2 size-reduce-1' href={additional_entry_data.base_event_url}>{t('go_to_base_event')}</a>
                    {/if}
                    {#if is_editing}
                      <button
                        type='button'
                        class='event-link text-control text-2 size-reduce-1'
                        onclick={() => { show_detach_dialog = true; }}
                      >
                        {t('detach_from_series')}
                      </button>
                    {/if}
                  {/if}
                {/if}
              </div>
            {/if}
            {#if entry.tag_keys.length > 0}
              <!-- Categories (tags with a colour) with their mark, other tags as plain words -->
              <p class='event-tags flex-wrap gap-x g-5'>
                {#each entry.tag_keys as tag_key}
                  {#if page.data.entry_tag_colors?.[tag_key]}
                    <CategoryLabel category={{ tag_key, color_id: page.data.entry_tag_colors[tag_key] }} text_style='text-preamble' wrap />
                  {:else}
                    <span class='text-preamble text-2'>{tag_key}</span>
                  {/if}
                {/each}
              </p>
            {/if}
            <div class='relative flex-grow {is_editing ? 'full-width' : ''}'>
              <Text
                editable={is_editing}
                placeholder={t('for_example_location')}
                bind:content={entry.subtitle}
                variant='preamble'
                multiline={true}
              />
            </div>
          </div>
        {/if}
        <div class='relative'>
          <Text
            editable={is_editing}
            variant='heading1'
            placeholder={t('title')}
            bind:content={entry.title}
          />
        </div>
      </div>

      <!---------------------------------------------------------------------------------->
      <!-------------------------- Ticket: date, price, seats, registration -------------->
      <!---------------------------------------------------------------------------------->
      <EventTicket
        bind:element={ticket_element}
        {entry}
        additional_entry_data={live_data}
        {is_editing}
        {is_admin}
        {is_restricted}
        show_availability={!is_restricted && !has_concluded}
      >
        {#if !is_admin || entry.registration_mode === 'external'}
          <!-- Admins see the external link too, so they can check where it leads -->
          <EventRegistration
            {entry}
            additional_entry_data={live_data}
            {capacity}
            {is_restricted}
            {has_concluded}
            open_form={open_registration_form}
            {confirming_payment}
            onchanged={handle_registration_changed}
            onrefresh={refresh_registrations}
          />
        {/if}
      </EventTicket>
    </div>
  </header>

  <!---------------------------------------------------------------------------------->
  <!-------------------------- Guest list (admin) ------------------------------------>
  <!---------------------------------------------------------------------------------->
  <!-- Editing the page hides the list (the two surfaces don't mix), but keeps it
       mounted: unmounting would discard a half-typed guest row and orphan a
       running bulk-email job. The dialog is a modal, always available. -->
  {#if is_admin && entry.entry_id && (entry.registration_mode === 'internal' || (live_data?.event_registrations?.length ?? 0) > 0)}
    <div class='event-guest-list-container' hidden={is_editing}>
      <EventGuestList
        {entry}
        additional_entry_data={live_data}
        on_email_all={() => email_dialog?.open()}
        onrefresh={refresh_registrations}
      />
    </div>
    {#if email_dialog_component.component}
      <email_dialog_component.component {entry} additional_entry_data={live_data} bind:this={email_dialog} />
    {/if}
  {/if}

  <!---------------------------------------------------------------------------------->
  <!-------------------------- Detach from Series Dialog ----------------------------->
  <!---------------------------------------------------------------------------------->
  {#if show_detach_dialog}
    <Dialog onclose={() => { show_detach_dialog = false; }}>
      <div class='flex-column gap-y g-4'>
        <h2 class='text-heading3'>{t('detach_from_series_title')}</h2>
        <p class='text-control text-2'>
          {t('detach_from_series_description')}
        </p>
        <div class='flex-row justify-between gap-x g-4'>
          <button onclick={() => { show_detach_dialog = false; }}>
            <ButtonLabel variant={`brand ${site_settings.secondary_control_surface}`}>
              {t('cancel')}
            </ButtonLabel>
          </button>
          <button onclick={detach_from_series}>
            <ButtonLabel variant={`brand ${site_settings.primary_control_surface}`}>
              {t('detach_from_series_confirm')}
            </ButtonLabel>
          </button>
        </div>
      </div>
    </Dialog>
  {/if}

  <!---------------------------------------------------------------------------------->
  <!--------------------------------- Event Body ------------------------------------>
  <!---------------------------------------------------------------------------------->
  {#if entry.body.blocks.length > 0 || is_editing}
    {#if is_editing}
      <div class='event-body-notice flex-column items-center justify-center'>
        <Alert icon='pencil' variant='brand'>
          <p class='text-control text-2'>{t('event_body_notice')}</p>
        </Alert>
      </div>
    {/if}
    <footer class='event-story'>
      <!-- with bind:this we link the model with the component instance -->
      <Container
        container={entry.body}
        bind:this={entry.body.ref}
      />
    </footer>
  {/if}

</BlockGrid>

<style lang='postcss'>
  .event-tags {
    display: flex;
    max-width: 100%;
    margin: 0;
  }
  .event-header, .cover-container, .event-guest-list-container {
    grid-column: var(--track-span-extended1);
  }
  /* A title of one long word (German compounds) hyphenates instead of widening the page grid */
  .event-header, .event-text-column {
    min-width: 0;
  }
  .event-body-notice {
    grid-column: var(--track-span-extended1);
    margin-block-start: var(--block-spacing-m);
  }
  /* The guest list already keeps its distance from what follows */
  .event-guest-list-container + .event-body-notice {
    margin-block-start: 0;
  }
  .event-story {
    grid-column: 1 / -1;
    margin-block-end: var(--block-spacing-m);
  }
  /* The header's layout (poster, title, ticket) is in event-ui.css */
  .event-header :global(*) {
    text-align: left;
  }
  /* Same distance to the body as the header keeps. Long headings and
     rows shrink to the column instead of widening the page grid */
  .event-guest-list-container {
    min-width: 0;
    margin-block-end: var(--block-spacing-s);
  }
</style>
